import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useGetLiveContextStateQuery } from '@/state/queries/player/use-get-live-context-state';
import { useHydratePerformControlFromGet } from '@/hooks/use-hydrate-perform-control-from-get';
import { useMergedCueState } from '@/hooks/use-merged-cue-state';
import {
  acquirePerformControlRoom,
  entityRoomKeyFromEntityRef,
  usePerformControlWsStore,
} from '@/state/player/perform-control-ws.store';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import type { FlowCueStateV1, LiveCueSheetV1, PerformControlV1 } from '@/types/player/perform-control-v1.types.ts';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';

type FlowControlLiveStateValue = {
  entityRef: string | undefined;
  performMode: FlowControlPerformMode;
  liveContextState: LiveContextStateDto | undefined;
  liveContextQueryUpdatedAt: number;
  cueState: FlowCueStateV1 | undefined;
  cueSheet: LiveCueSheetV1 | undefined;
  performControl: PerformControlV1 | undefined;
};

const FlowControlLiveStateContext = createContext<FlowControlLiveStateValue | null>(null);

const EMPTY_LIVE_STATE: FlowControlLiveStateValue = {
  entityRef: undefined,
  performMode: 'simulation',
  liveContextState: undefined,
  liveContextQueryUpdatedAt: 0,
  cueState: undefined,
  cueSheet: undefined,
  performControl: undefined,
};

/**
 * Single owner for Control/Perform live-state: join GET + WS apply + refcounted room reset.
 * Children read the store / this hook; they do not GET or hydrate.
 */
export function FlowControlLiveStateProvider({
  entityRef,
  performMode,
  children,
}: {
  entityRef: string | undefined;
  performMode: FlowControlPerformMode;
  children: ReactNode;
}) {
  const roomKey = entityRoomKeyFromEntityRef(entityRef ?? '');
  const { data: liveContextState, dataUpdatedAt: liveContextQueryUpdatedAt } = useGetLiveContextStateQuery(entityRef, {
    enabled: !!entityRef && performMode === 'play',
  });

  useEffect(() => {
    if (!roomKey) {
      return;
    }
    return acquirePerformControlRoom(roomKey);
  }, [roomKey]);

  useHydratePerformControlFromGet(
    entityRef,
    liveContextState?.performControl,
    liveContextQueryUpdatedAt,
    liveContextState?.cueState,
    liveContextState?.cueSheet,
    liveContextState?.mediaSequenceIndex,
    liveContextState?.mediaAgentRecovery,
  );

  const cueState = useMergedCueState(entityRef, liveContextState?.cueState);
  const cueSheet = usePerformControlWsStore((s) => (roomKey ? s.cueSheetByRoom[roomKey] : undefined));
  const storeAggregate = usePerformControlWsStore((s) => (roomKey ? s.aggregateByRoom[roomKey] : undefined));
  const storeItems = usePerformControlWsStore((s) => (roomKey ? s.itemsByRoom[roomKey] : undefined));
  const performControl = useMemo((): PerformControlV1 | undefined => {
    if (storeAggregate) {
      return { version: 1 as const, aggregate: storeAggregate, items: storeItems ?? {} };
    }
    return liveContextState?.performControl;
  }, [liveContextState?.performControl, storeAggregate, storeItems]);

  const value = useMemo(
    (): FlowControlLiveStateValue => ({
      entityRef,
      performMode,
      liveContextState,
      liveContextQueryUpdatedAt,
      cueState,
      cueSheet,
      performControl,
    }),
    [cueSheet, cueState, entityRef, liveContextQueryUpdatedAt, liveContextState, performControl, performMode],
  );

  return <FlowControlLiveStateContext.Provider value={value}>{children}</FlowControlLiveStateContext.Provider>;
}

export function useFlowControlLiveState(): FlowControlLiveStateValue {
  return useContext(FlowControlLiveStateContext) ?? EMPTY_LIVE_STATE;
}
